import type {
    AlbumType,
    ArtworkKind,
    JournalCategory,
    MilestoneKind,
    SongFormat,
    StreamingLinks,
    VideoType
} from '@/types/content'

export const songFormatLabels: Record<SongFormat, string> = {
    single: 'Single',
    album: 'Album track',
    ep: 'EP track'
}

export const albumTypeLabels: Record<AlbumType, string> = {
    album: 'Album',
    ep: 'EP',
    single: 'Single',
    compilation: 'Compilation'
}

export const videoTypeLabels: Record<VideoType, string> = {
    official: 'Official music video',
    lyric: 'Lyric video',
    visualiser: 'Visualiser',
    animated: 'Animated music',
    short_film: 'Short film',
    experimental: 'Experimental visual'
}

export const journalCategoryLabels: Record<JournalCategory, string> = {
    poetry: 'Original poetry',
    philosophy: 'Philosophical thoughts',
    shayari: 'Shayari & ghazals',
    behind_the_lyrics: 'Behind the lyrics',
    songwriting: 'Songwriting stories',
    reflection: 'Short reflections',
    experiment: 'Creative experiments'
}

export const milestoneKindLabels: Record<MilestoneKind, string> = {
    release: 'Release',
    milestone: 'Milestone',
    performance: 'Performance',
    collaboration: 'Collaboration',
    press: 'Press'
}

export const artworkKindLabels: Record<ArtworkKind, string> = {
    cover: 'Album cover',
    visual_art: 'Visual art',
    photography: 'Photography',
    illustration: 'Illustration'
}

export const statusLabels: Record<string, string> = {
    draft: 'Draft',
    scheduled: 'Scheduled',
    published: 'Published'
}

export const streamingPlatforms: Array<{ key: keyof StreamingLinks, label: string }> = [
    { key: 'spotify_url', label: 'Spotify' },
    { key: 'apple_music_url', label: 'Apple Music' },
    { key: 'youtube_music_url', label: 'YouTube Music' },
    { key: 'jiosaavn_url', label: 'JioSaavn' },
    { key: 'amazon_music_url', label: 'Amazon Music' },
    { key: 'soundcloud_url', label: 'SoundCloud' }
]

export const languageCodes: Record<string, string> = {
    english: 'en',
    hindi: 'hi',
    kannada: 'kn',
    urdu: 'ur',
    tamil: 'ta',
    telugu: 'te',
    malayalam: 'ml',
    marathi: 'mr',
    bengali: 'bn',
    punjabi: 'pa',
    gujarati: 'gu',
    sanskrit: 'sa',
    tulu: 'tcy',
    konkani: 'kok',
    odia: 'or',
    spanish: 'es',
    french: 'fr',
    german: 'de',
    italian: 'it',
    portuguese: 'pt',
    japanese: 'ja',
    korean: 'ko',
    arabic: 'ar',
    persian: 'fa'
}

export const commonLanguages = ['English', 'Kannada', 'Hindi', 'Urdu', 'Tamil', 'Telugu', 'Malayalam', 'Marathi', 'Bengali', 'Punjabi', 'Sanskrit', 'Tulu']

/** Returns a BCP-47 code for a language name, or undefined when it is not known. */
export const languageCode = (language: string | null | undefined): string | undefined => {
    if (!language) return undefined

    const key = language.trim().toLowerCase().split(/[\s(]/)[0]

    return languageCodes[key]
}

/** Right-to-left scripts need the dir attribute on lyric and poem containers. */
export const languageDirection = (language: string | null | undefined): 'rtl' | 'ltr' => {
    const code = languageCode(language)

    return code === 'ur' || code === 'ar' || code === 'fa' ? 'rtl' : 'ltr'
}
